import { useState, useEffect } from "react";
import { formatObject } from "../common/formatting";
import { useTranslation } from "../i18n";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "./ui/table";

function SortingIcon({ rotated, isActive }) {
  return (
    <svg
      className={`h-2 fill-current ${isActive ? "text-white" : "text-c-gray-400"} ${rotated ? "rotate-180" : ""}`}
      version="1.1"
      viewBox="0 0 330 330"
    >
      <path
        d="M325.606,229.393l-150.004-150C172.79,76.58,168.974,75,164.996,75c-3.979,0-7.794,1.581-10.607,4.394
	l-149.996,150c-5.858,5.858-5.858,15.355,0,21.213c5.857,5.857,15.355,5.858,21.213,0l139.39-139.393l139.397,139.393
	C307.322,253.536,311.161,255,315,255c3.839,0,7.678-1.464,10.607-4.394C331.464,244.748,331.464,235.251,325.606,229.393z"
      />
    </svg>
  );
}

function SortButtons({ onClick, isActive, direction }) {
  return (
    <button onClick={onClick} type="button">
      <div className="flex flex-col space-y-1">
        <SortingIcon
          rotated={false}
          isActive={isActive && direction === "ascending"}
        />
        <SortingIcon
          rotated={true}
          isActive={isActive && direction === "descending"}
        />
      </div>
    </button>
  );
}

function defaultRenderContextMenu(_row, children) {
  return <>{children}</>;
}

function getValue(column, row) {
  if (column.accessor) {
    return typeof column.accessor === "function"
      ? column.accessor(row)
      : row[column.accessor];
  }
  return null;
}

function sortRows(rows, column, direction) {
  return [...(rows || [])].sort((a, b) => {
    let lhs = getValue(column, a);
    let rhs = getValue(column, b);
    if (lhs == null) return 1;
    if (rhs == null) return -1;
    if (lhs < rhs) return direction === "ascending" ? -1 : 1;
    if (lhs > rhs) return direction === "ascending" ? 1 : -1;
    return 0;
  });
}

export default function DataTable({
  columns,
  data,
  className,
  onRowClick,
  renderContextMenu = defaultRenderContextMenu,
}) {
  const { t } = useTranslation();
  const [sortedBy, setSortedBy] = useState(columns[0]?.header || "");
  const [sortDirection, setSortDirection] = useState("ascending");
  const [innerRows, setInnerRows] = useState(data || []);

  useEffect(() => {
    setInnerRows(data || []);
  }, [data]);

  // Guard: ensure rows contains only defined items
  const safeRows = Array.isArray(innerRows) ? innerRows.filter(Boolean) : [];

  const handleSort = (column) => {
    const newDirection =
      sortedBy === column.header
        ? sortDirection === "ascending"
          ? "descending"
          : "ascending"
        : "ascending";
    setSortedBy(column.header);
    setSortDirection(newDirection);
    setInnerRows(sortRows(safeRows, column, newDirection));
  };

  return (
    <div
      className={`p-2 w-full bg-c-gray-600 rounded-lg flex flex-col overflow-auto ${className || ""}`}
    >
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((col, i) => (
              <TableHead key={i}>
                <div className="flex flex-row items-center cursor-pointer mr-2">
                  <span
                    className="mr-2 cursor-pointer select-none"
                    onClick={() => handleSort(col)}
                  >
                    {t(col.header)}
                  </span>
                  <SortButtons
                    onClick={() => handleSort(col)}
                    isActive={col.header === sortedBy}
                    direction={sortDirection}
                  />
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {!safeRows || safeRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length}>
                {t("common.noData")}
              </TableCell>
            </TableRow>
          ) : (
            safeRows.map((row) =>
              renderContextMenu(
                row,
                <TableRow
                  className={
                    onRowClick ? "hover:bg-c-gray-500 cursor-pointer" : ""
                  }
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((col, j) => (
                    <TableCell key={j}>
                      {formatObject(
                        getValue(col, row),
                        col.type || "text",
                        col.mapper,
                        col.options,
                      )}
                    </TableCell>
                  ))}
                </TableRow>,
              ),
            )
          )}
        </TableBody>
      </Table>
    </div>
  );
}
